// src/components/features/brands/forms/BrandIdentityForm.jsx
import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  Button,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Switch,
  FormControlLabel,
  CardContent,
  useTheme,
  alpha,
  MenuItem,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ColorLens as ColorIcon,
  TextFields as FontIcon,
  Upload as UploadIcon,
  Check as CheckIcon,
  Style as StyleIcon,
} from '@mui/icons-material';

// Import custom components
import BrandLogoUpload from './BrandLogoUpload';
import { Card } from '../../../design-system';
import { FormSection } from '../../../design-system/components/forms';

/**
 * Brand visual identity form component
 * Handles logos, color palette, and typography
 */
const BrandIdentityForm = ({ 
  brandData, 
  data, // Support for the new data prop pattern
  onChange, 
  onNestedChange,
  setFieldValue, // Added for Formik support
  onLogoUpload,
  onLogoSelect,
  isEditMode = false,
  isUploading = false,
  errors = {},
  touched = {},
  requiredFields = []
}) => {
  const theme = useTheme();
  
  // Support both brandData and data prop patterns
  const formData = data || brandData || {};
  
  // Local state for color and font forms
  const [newColor, setNewColor] = useState({ hex: '#000000', name: '', isPrimary: false });
  const [newFont, setNewFont] = useState({ name: '', url: '', category: 'heading' });
  
  // Get colors and fonts from form data or initialize empty arrays
  // Ensure colors and fonts are always arrays even if they come in as undefined or null
  const colors = Array.isArray(formData.colors) ? formData.colors : [];
  const fonts = Array.isArray(formData.fonts) ? formData.fonts : [];
  
  // Check if there is at least one primary color - this helps with validation
  const hasPrimaryColor = colors.some(color => color.isPrimary === true);
  
  // Format errors for better display - with enhanced error handling for primary colors
  const formattedErrors = {
    ...errors,
    // Add any specific nested field error formatting here
    logo: errors['logo'] || errors['logo.primary'],
    // Override primary color error if we have a primary color - with more robust handling
    colors: hasPrimaryColor ? 
      (errors['colors'] === 'At least one primary color is required' ? null : 
       (errors['colors.primary'] === 'At least one primary color is required' ? null :
        (errors['colors.exist'] ? null : errors['colors']))) : 
      (errors['colors'] || errors['colors.primary'] || errors['colors.exist'])
  };
  
  // Force clear color-related errors if we actually have a primary color
  // This provides an additional safeguard against persistent errors
  if (hasPrimaryColor && colors.length > 0) {
    if (formattedErrors.colors && 
        (formattedErrors.colors.includes('primary color') || 
         formattedErrors.colors.includes('color is required'))) {
      formattedErrors.colors = null;
    }
  }

  // Format touched state for validation
  const formattedTouched = {
    ...touched,
    logo: touched['logo'] || touched['logo.primary'],
    colors: touched['colors'] || touched['colors.primary']
  };
  
  // Check if we have at least one logo (to help with validation UI)
  const hasAnyLogo = formData.logo && (
    (formData.logo.primary && 
      (typeof formData.logo.primary === 'string' || 
       (typeof formData.logo.primary === 'object' && formData.logo.primary.url))) ||
    (formData.logo.secondary && 
      (typeof formData.logo.secondary === 'string' || 
       (typeof formData.logo.secondary === 'object' && formData.logo.secondary.url))) ||
    (formData.logo.favicon && 
      (typeof formData.logo.favicon === 'string' || 
       (typeof formData.logo.favicon === 'object' && formData.logo.favicon.url)))
  );
  
  // Handle adding a new color
  const handleAddColor = () => {
    // Don't add if name is empty
    if (!newColor.name.trim()) return;
    
    // Ensure colors is always an array before attempting to spread it
    const updatedColors = Array.isArray(colors) ? [...colors] : [];
    
    // Create a new color object with a unique ID to help with identification
    const newColorObj = {
      ...newColor,
      id: Date.now().toString(), // Add a unique ID for each color
      // Make sure to set isPrimary to true if this is the first color, regardless of checkbox
      isPrimary: newColor.isPrimary || updatedColors.length === 0
    };
    
    // If this is marked as primary, update other colors
    if (newColorObj.isPrimary) {
      updatedColors.forEach(color => {
        color.isPrimary = false;
      });
    }
    
    // Add the new color
    updatedColors.push(newColorObj);
    
    // Removed this section since we're already setting isPrimary in the newColorObj
    // and we don't want to override the flag here
    
    console.log('Adding color:', newColorObj);
    console.log('Updated colors array:', updatedColors);
    
    // Debug log to check the primary color status
    const hasPrimary = updatedColors.some(c => c.isPrimary === true);
    console.log('Has primary color after adding:', hasPrimary);
    console.log('Primary color in array:', updatedColors.find(c => c.isPrimary));
    
    // Update form data - we need to use a different approach to ensure the update persists
    if (setFieldValue) {
      // Use setFieldValue directly without additional validation logic that might interfere
      setFieldValue('colors', updatedColors);
      
      // Explicitly clear primary color validation error if we have a primary color
      if (hasPrimary && errors && (errors.colors === 'At least one primary color is required')) {
        setFieldValue('colors.primary', true);
      }
    } else {
      // For non-Formik forms, use the onChange handler
      onChange('colors', [...updatedColors]); // Use spread to ensure a new array reference
    }
    
    // Reset form after a small delay to ensure the update happens first
    setTimeout(() => {
      setNewColor({ hex: '#000000', name: '', isPrimary: false });
    }, 100);
  };
  
  // Handle deleting a color
  const handleDeleteColor = (index) => {
    // Ensure colors is always an array before attempting to spread it
    const updatedColors = Array.isArray(colors) ? [...colors] : [];
    
    console.log('Deleting color at index:', index);
    console.log('Colors before deletion:', updatedColors);
    
    if (updatedColors.length > index) {
      const removedColor = updatedColors.splice(index, 1)[0];
      
      // If we're removing the primary color, make the first remaining color primary
      if (removedColor && removedColor.isPrimary && updatedColors.length > 0) {
        updatedColors[0].isPrimary = true;
      }
      
      console.log('Colors after deletion:', updatedColors);
      
      // Update form data with the new array
      if (setFieldValue) {
        // Use setFieldValue directly to update the colors array
        setFieldValue('colors', [...updatedColors]);
      } else {
        // For non-Formik forms, use the onChange handler with a new array reference
        onChange('colors', [...updatedColors]);
      }
    }
  };
  
  // Handle setting a color as primary
  const handleSetPrimaryColor = (index) => {
    // Ensure colors is an array before calling map
    if (!Array.isArray(colors)) {
      return; // Can't set primary if colors isn't an array
    }
    
    console.log('Setting primary color at index:', index);
    
    const updatedColors = colors.map((color, i) => ({
      ...color,
      isPrimary: i === index
    }));
    
    console.log('Colors after setting primary:', updatedColors);
    
    if (setFieldValue) {
      // Use setFieldValue to update the colors array
      setFieldValue('colors', [...updatedColors]);
    } else {
      // For non-Formik forms, use the onChange handler with a new array reference
      onChange('colors', [...updatedColors]);
    }
  };
  
  // Handle adding a new font
  const handleAddFont = () => {
    // Don't add if name is empty
    if (!newFont.name.trim()) return;
    
    const updatedFonts = [...fonts, {...newFont}];
    
    if (setFieldValue) {
      setFieldValue('fonts', updatedFonts);
    } else {
      onChange('fonts', updatedFonts);
    }
    
    // Reset form
    setNewFont({ name: '', url: '', category: 'heading' });
  };
  
  // Handle deleting a font
  const handleDeleteFont = (index) => {
    const updatedFonts = [...fonts];
    updatedFonts.splice(index, 1);
    
    if (setFieldValue) {
      setFieldValue('fonts', updatedFonts);
    } else {
      onChange('fonts', updatedFonts);
    }
  };
  
  // Handle logo upload or selection
  const handleLogoAction = (logoType, file) => {
    if (isEditMode && onLogoUpload) {
      // In edit mode, upload directly
      onLogoUpload(formData._id, logoType, file);
    } else if (onLogoSelect) {
      // In create mode, select for later upload
      onLogoSelect(logoType, file);
    }
    
    // Clear logo validation error since we know a logo is being added
    if (formattedErrors.logo && setFieldValue) {
      // Use setTimeout to ensure the state update has time to complete
      setTimeout(() => {
        // Mark logo as touched to trigger validation
        setFieldValue('logo.touched', true, false);
      }, 100);
    }
  };
  
  // Handle logo removal
  const handleLogoRemove = (logoType) => {
    const updatedLogo = {...formData.logo};
    delete updatedLogo[logoType];
    
    if (setFieldValue) {
      setFieldValue('logo', updatedLogo);
    } else {
      onChange('logo', updatedLogo);
    }
  };
  
  // Check for validation errors
  const hasLogoError = Boolean(formattedErrors.logo);
  const hasColorError = Boolean(formattedErrors.colors);
  const hasFontError = Boolean(formattedErrors.fonts);
  
  return (
    <Grid container spacing={4}>
      {/* Logo Uploads Section */}
      <Grid item xs={12}>
        <FormSection
          title="Brand Logos"
          description="Upload and manage your brand logos for different contexts"
          icon={<StyleIcon />}
        >
          {hasLogoError && formattedTouched.logo && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {formattedErrors.logo}
            </Alert>
          )}
          
          <Grid container spacing={3}>
            {/* Primary Logo */}
            <Grid item xs={12} md={4}>
              <BrandLogoUpload
                title="Primary Logo"
                description="Main logo for light backgrounds"
                logo={formData.logo?.primary}
                onUpload={(file) => handleLogoAction('primary', file)}
                onRemove={() => handleLogoRemove('primary')}
                isUploading={isUploading}
                required={requiredFields.includes('logo.primary')}
                error={Boolean(formattedErrors.logo)}
              />
            </Grid>
            
            {/* Secondary Logo */}
            <Grid item xs={12} md={4}>
              <BrandLogoUpload
                title="Secondary Logo"
                description="Alternative logo for dark backgrounds"
                logo={formData.logo?.secondary}
                onUpload={(file) => handleLogoAction('secondary', file)}
                onRemove={() => handleLogoRemove('secondary')}
                isUploading={isUploading}
              />
            </Grid>
            
            {/* Favicon */}
            <Grid item xs={12} md={4}>
              <BrandLogoUpload
                title="Favicon"
                description="Small icon for browser tabs"
                logo={formData.logo?.favicon}
                onUpload={(file) => handleLogoAction('favicon', file)}
                onRemove={() => handleLogoRemove('favicon')}
                isUploading={isUploading}
                maxSize={64}
              />
            </Grid>
          </Grid>
        </FormSection>
      </Grid>
      
      {/* Color Palette Section */}
      <Grid item xs={12}>
        <FormSection
          title="Color Palette"
          description="Define the brand's color scheme and identify primary/secondary colors"
          icon={<ColorIcon />}
        >
          {hasColorError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {formattedErrors.colors}
            </Alert>
          )}
        
          {/* Color List */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {colors.map((color, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card level={color.isPrimary ? 2 : 1} magical={color.isPrimary}>
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '8px',
                          backgroundColor: color.hex,
                          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                          mr: 2,
                        }}
                      />
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle2">
                          {color.name}
                          {color.isPrimary && (
                            <Chip 
                              label="Primary" 
                              size="small" 
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {color.hex.toUpperCase()}
                        </Typography>
                      </Box>
                      <Box>
                        {!color.isPrimary && (
                          <IconButton
                            size="small"
                            onClick={() => handleSetPrimaryColor(index)}
                            title="Set as primary color"
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteColor(index)}
                          title="Delete color"
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {/* Add Color Form */}
          <Card 
            sx={{ 
              mb: 3,
              border: !hasPrimaryColor && Boolean(formattedErrors.colors) && formattedTouched.colors ? 
                `1px solid ${theme.palette.error.main}` : undefined,
              backgroundColor: !hasPrimaryColor && Boolean(formattedErrors.colors) && formattedTouched.colors ?
                alpha(theme.palette.error.main, 0.05) : undefined
            }}
          >
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Add New Color
                {((requiredFields.includes('colors') || 
                  requiredFields.includes('colors.primary') || 
                  requiredFields.includes('colors.exist')) && !hasPrimaryColor) && (
                  <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>*</Box>
                )}
              </Typography>
              {!hasPrimaryColor && Boolean(formattedErrors.colors) && formattedTouched.colors && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {formattedErrors.colors}
                </Alert>
              )}
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <TextField
                    type="color"
                    value={newColor.hex}
                    onChange={(e) => setNewColor({...newColor, hex: e.target.value})}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ColorIcon />
                        </InputAdornment>
                      ),
                    }}
                    error={!hasPrimaryColor && Boolean(formattedErrors.colors) && formattedTouched.colors}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    placeholder="Color Name"
                    value={newColor.name}
                    onChange={(e) => setNewColor({...newColor, name: e.target.value})}
                    fullWidth
                    error={!hasPrimaryColor && Boolean(formattedErrors.colors) && formattedTouched.colors}
                    helperText={!hasPrimaryColor && formattedTouched.colors && formattedErrors.colors || ''}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newColor.isPrimary}
                        onChange={(e) => setNewColor({...newColor, isPrimary: e.target.checked})}
                      />
                    }
                    label="Primary Color"
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddColor}
                    fullWidth
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </FormSection>
      </Grid>
      
      {/* Typography Section */}
      <Grid item xs={12}>
        <FormSection
          title="Typography"
          description="Specify the fonts used in brand communications"
          icon={<FontIcon />}
        >
          {hasFontError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {formattedErrors.fonts}
            </Alert>
          )}
          
          {/* Font List */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {fonts.map((font, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card sx={{ 
                  height: '100%',
                  border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
                  boxShadow: `0 2px 4px ${alpha(theme.palette.common.black, 0.04)}`
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2">
                        {font.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteFont(index)}
                        title="Delete font"
                      >
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Box>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                      {font.category?.charAt(0)?.toUpperCase() + font.category?.slice(1)}
                    </Typography>
                    {font.url && (
                      <Typography variant="body2" noWrap sx={{ wordBreak: 'break-all' }}>
                        {font.url}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          {/* Add Font Form */}
          <Card
            sx={{
              mb: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
              boxShadow: `0 1px 3px ${alpha(theme.palette.common.black, 0.04)}`
            }}
          >
            <CardContent>
              <Typography variant="subtitle2" gutterBottom>
                Add New Font
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <TextField
                    placeholder="Font Name"
                    value={newFont.name}
                    onChange={(e) => setNewFont({...newFont, name: e.target.value})}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FontIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    select
                    label="Category"
                    value={newFont.category}
                    onChange={(e) => setNewFont({...newFont, category: e.target.value})}
                    fullWidth
                  >
                    <MenuItem value="heading">Heading</MenuItem>
                    <MenuItem value="body">Body</MenuItem>
                    <MenuItem value="accent">Accent</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    placeholder="Font URL (optional)"
                    value={newFont.url}
                    onChange={(e) => setNewFont({...newFont, url: e.target.value})}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddFont}
                    fullWidth
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </FormSection>
      </Grid>
    </Grid>
  );
};

export default BrandIdentityForm;